import { useState, type CSSProperties, type ReactNode } from 'react'
import {
  NewspaperIcon,
  BookmarkIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  ClockIcon,
  HomeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import type { NewsBlock, NewsCategory, NewsAudience } from '../../api/news'
import BlockRenderer from './BlockRenderer'
import { CATEGORIES, alpha, APP_LIGHT, APP_DARK, PORTAL, type SurfaceTokens } from './newsTheme'

// Live previews of the item being composed, copied from the approved mockup:
// the RN app's list, article and home (light and dark, Tamagui tokens) and the
// admin portal's News panel. All of them draw content through BlockRenderer.

export interface PreviewItem {
  id: string
  title: string
  summary: string
  category: NewsCategory
  version: string | null
  blocks: NewsBlock[]
  cover_image_url: string | null
  pinned: boolean
  priority: 'normal' | 'high'
  audience: NewsAudience
  date: string
  unread: boolean
  sample?: boolean
}

const SAMPLES: PreviewItem[] = [
  {
    id: 's-fix', title: 'Fixes in 4.0.27', summary: 'Break reminders no longer repeat, and drop-off photos upload faster on weak signal.',
    category: 'fix', version: '4.0.27', pinned: false, priority: 'normal', audience: 'both', date: 'Sep 22', unread: false, sample: true,
    cover_image_url: null, blocks: [{ type: 'list', items: ['Break reminders no longer repeat', 'Faster photo upload on weak signal'] }],
  },
  {
    id: 's-tip', title: 'Tip: long-press a delivery to change the truck', summary: 'Saves a few taps when you switch trucks mid-shift.',
    category: 'tip', version: null, pinned: false, priority: 'normal', audience: 'app', date: 'Sep 20', unread: false, sample: true,
    cover_image_url: null, blocks: [{ type: 'paragraph', text: 'Press and hold any delivery card, then choose **Change truck**.' }],
  },
]

function Chip({ category, size = 36, radius = 11 }: { category: NewsCategory; size?: number; radius?: number }) {
  const c = CATEGORIES[category]
  return (
    <span style={{ width: size, height: size, borderRadius: radius, background: alpha(c.color, 0.16), display: 'grid', placeItems: 'center', flex: 'none' }}>
      <c.Icon style={{ width: Math.round(size * 0.5), height: Math.round(size * 0.5), color: c.color }} />
    </span>
  )
}

function CategoryPill({ category }: { category: NewsCategory }) {
  const c = CATEGORIES[category]
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 8px', background: alpha(c.color, 0.12), color: c.color, whiteSpace: 'nowrap' }}>
      <c.Icon style={{ width: 11, height: 11 }} />{c.label}
    </span>
  )
}

function Cover({ item, height, radius = 0 }: { item: PreviewItem; height: number; radius?: number }) {
  const color = CATEGORIES[item.category].color
  if (item.cover_image_url) {
    return <img src={item.cover_image_url} alt="" style={{ display: 'block', width: '100%', height, objectFit: 'cover', borderRadius: radius }} />
  }
  return <div style={{ height, borderRadius: radius, background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.5)})` }} />
}

function PinBanner({ item }: { item: PreviewItem }) {
  return (
    <div style={{ borderRadius: 16, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start', color: '#fff', background: CATEGORIES[item.category].color }}>
      <BookmarkIcon style={{ width: 16, height: 16, flex: 'none', marginTop: 2 }} />
      <div>
        <b style={{ fontSize: 13 }}>{item.title || 'Untitled'}</b>
        <small style={{ display: 'block', opacity: 0.9, fontSize: 11.5 }}>{item.summary}</small>
      </div>
    </div>
  )
}

function Phone({ t, children, tabbar = false }: { t: SurfaceTokens; children: ReactNode; tabbar?: boolean }) {
  return (
    <div style={{ width: 300, height: 620, borderRadius: 44, background: '#111', padding: 10, flex: 'none' }}>
      <div style={{ width: '100%', height: '100%', borderRadius: 36, overflow: 'hidden', background: t.bg, color: t.color, display: 'flex', flexDirection: 'column', fontSize: 13 }}>
        <div style={{ height: 36, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 22px', fontWeight: 600, fontSize: 12.5, flex: 'none' }}>
          <span>9:41</span><span>5G</span>
        </div>
        {children}
        {tabbar && (
          <nav style={{ display: 'flex', borderTop: `1px solid ${t.border}`, background: t.card, padding: '6px 4px 16px', flex: 'none' }}>
            {[['Home', HomeIcon, true], ['Shift', ClockIcon, false], ['News', NewspaperIcon, false]].map(([label, Icon, on]) => {
              const I = Icon as typeof HomeIcon
              return (
                <span key={label as string} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontWeight: 600, fontSize: 10.5, color: on ? t.primary : t.secondary }}>
                  <I style={{ width: 19, height: 19 }} />{label as string}
                </span>
              )
            })}
          </nav>
        )}
      </div>
    </div>
  )
}

const bodyStyle: CSSProperties = { flex: 1, overflow: 'auto', padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }

function AppCard({ item, t, onOpen }: { item: PreviewItem; t: SurfaceTokens; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} style={{ background: t.card, border: `1px solid ${item.unread ? t.primary : t.border}`, borderRadius: 18, overflow: 'hidden', cursor: 'pointer', textAlign: 'left', color: 'inherit', font: 'inherit', width: '100%', padding: 0, opacity: item.sample ? 0.75 : 1 }}>
      {item.cover_image_url ? <Cover item={item} height={90} /> : null}
      <div style={{ padding: 12, display: 'flex', gap: 10 }}>
        <Chip category={item.category} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
            {item.unread && <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.primary, flex: 'none' }} />}
            {item.title || 'Untitled'}
          </div>
          <div style={{ fontSize: 12.5, color: t.secondary, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.summary}</div>
          <div style={{ fontSize: 10.5, color: t.secondary, fontWeight: 600, marginTop: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
            {item.version && <span style={{ border: `1px solid ${t.primary}`, color: t.primary, borderRadius: 8, padding: '1px 6px', fontWeight: 800 }}>v{item.version}</span>}
            <span>{CATEGORIES[item.category].label}</span><span>·</span><span>{item.date}</span>
          </div>
        </div>
      </div>
    </button>
  )
}

function AppList({ items, t, onOpen }: { items: PreviewItem[]; t: SurfaceTokens; onOpen: (id: string) => void }) {
  const banner = items.find((x) => x.pinned && x.priority === 'high')
  const unread = items.filter((x) => x.unread).length
  return (
    <Phone t={t}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px 10px', flex: 'none' }}>
        <span style={{ width: 36, height: 36, borderRadius: 11, display: 'grid', placeItems: 'center', background: 'rgba(255,107,0,.16)' }}><NewspaperIcon style={{ width: 18, height: 18, color: '#ff6b00' }} /></span>
        <b style={{ fontSize: 17 }}>News</b>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: t.secondary, fontWeight: 600 }}>{unread} unread</span>
      </div>
      <div style={bodyStyle}>
        {banner && <PinBanner item={banner} />}
        {items.filter((x) => x !== banner).map((x) => <AppCard key={x.id} item={x} t={t} onOpen={() => onOpen(x.id)} />)}
      </div>
    </Phone>
  )
}

function AppArticle({ item, t, onBack }: { item: PreviewItem; t: SurfaceTokens; onBack: () => void }) {
  const c = CATEGORIES[item.category]
  return (
    <Phone t={t}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px 10px', flex: 'none' }}>
        <button type="button" onClick={onBack} aria-label="Back" style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(120,120,128,.16)', display: 'grid', placeItems: 'center', color: t.secondary, border: 0, cursor: 'pointer' }}>
          <ArrowLeftIcon style={{ width: 18, height: 18 }} />
        </button>
        <b style={{ fontSize: 15 }}>News</b>
      </div>
      <div style={bodyStyle}>
        {item.cover_image_url && <Cover item={item} height={130} radius={18} />}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <CategoryPill category={item.category} />
          {item.version && <span style={{ border: `1px solid ${t.primary}`, color: t.primary, borderRadius: 8, padding: '1px 6px', fontWeight: 800, fontSize: 10.5 }}>v{item.version}</span>}
          <span style={{ fontSize: 11, color: t.secondary, fontWeight: 600 }}>{item.date}</span>
        </div>
        <h2 style={{ margin: 0, fontSize: 19, lineHeight: 1.25 }}>{item.title || 'Untitled'}</h2>
        {item.summary && <p style={{ color: t.secondary, margin: 0 }}>{item.summary}</p>}
        <BlockRenderer blocks={item.blocks} tokens={t} accent={c.color} compact />
      </div>
    </Phone>
  )
}

function AppHome({ items, t, onOpen }: { items: PreviewItem[]; t: SurfaceTokens; onOpen: (id: string) => void }) {
  const banner = items.find((x) => x.pinned && x.priority === 'high')
  const latest = items.find((x) => x.unread && x !== banner)
  const unread = items.filter((x) => x.unread).length
  return (
    <Phone t={t} tabbar>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px 10px', flex: 'none' }}>
        <b style={{ fontSize: 17 }}>Good morning, Dana</b>
        <span style={{ marginLeft: 'auto', position: 'relative', width: 32, height: 32, borderRadius: '50%', background: 'rgba(120,120,128,.16)', display: 'grid', placeItems: 'center', color: t.secondary }}>
          <NewspaperIcon style={{ width: 18, height: 18 }} />
          {unread > 0 && <span style={{ position: 'absolute', top: -4, right: -4, background: t.primary, color: '#fff', borderRadius: 9, fontSize: 9.5, fontWeight: 800, padding: '1px 5px', border: `2px solid ${t.bg}` }}>{unread}</span>}
        </span>
      </div>
      <div style={bodyStyle}>
        {banner && <PinBanner item={banner} />}
        {latest && (
          <button type="button" onClick={() => onOpen(latest.id)} style={{ background: t.card, border: `1px solid ${t.primary}`, borderRadius: 18, cursor: 'pointer', textAlign: 'left', color: 'inherit', font: 'inherit', width: '100%', padding: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
            <Chip category={latest.category} size={32} radius={10} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.primary, flex: 'none' }} />{latest.title || 'Untitled'}
              </div>
              <div style={{ fontSize: 12.5, color: t.secondary }}>New · tap to read</div>
            </div>
            <ChevronRightIcon style={{ width: 16, height: 16 }} />
          </button>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[['Clock in', ClockIcon], ['Time off', HomeIcon]].map(([label, Icon]) => {
            const I = Icon as typeof HomeIcon
            return (
              <div key={label as string} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, padding: 12, fontWeight: 600, fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <I style={{ width: 18, height: 18 }} />{label as string}
              </div>
            )
          })}
        </div>
        <div style={{ fontSize: 11.5, color: t.secondary, textAlign: 'center' }}>News is also on its own tab, before and after clock-in.</div>
      </div>
    </Phone>
  )
}

function PortalPreview({ items }: { items: PreviewItem[] }) {
  const [open, setOpen] = useState(true)
  const [detail, setDetail] = useState<string | null>(null)
  const t = PORTAL
  const banner = items.find((x) => x.pinned && x.priority === 'high')
  const unread = items.filter((x) => x.unread).length
  const current = detail ? items.find((x) => x.id === detail) : null
  const btn: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '4px 9px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#0f172a' }
  return (
    <div style={{ width: '100%', background: t.bg, border: '1px solid #e2e8f0', borderRadius: 16, overflow: 'hidden', position: 'relative', minHeight: 560, color: t.color }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
        <b style={{ fontSize: 15 }}>CITS Admin</b><span style={{ color: '#6b7280', fontSize: 13 }}>Dashboard</span>
        <button type="button" onClick={() => { setOpen(!open); setDetail(null) }} aria-label="News" style={{ marginLeft: 'auto', position: 'relative', width: 36, height: 36, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', color: '#374151' }}>
          <NewspaperIcon style={{ width: 18, height: 18 }} />
          {unread > 0 && <span style={{ position: 'absolute', top: -5, right: -5, background: '#f97316', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 800, padding: '1px 5px', border: '2px solid #fff' }}>{unread}</span>}
        </button>
      </div>
      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
        {banner && (
          <div style={{ gridColumn: '1 / -1', borderRadius: 14, padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'center', border: `1px solid ${alpha(CATEGORIES[banner.category].color, 0.35)}`, background: alpha(CATEGORIES[banner.category].color, 0.08) }}>
            <Chip category={banner.category} size={36} radius={10} />
            <div><b>{banner.title || 'Untitled'}</b><div style={{ fontSize: 12.5, color: '#6b7280' }}>{banner.summary}</div></div>
            <button type="button" style={{ ...btn, marginLeft: 'auto' }} onClick={() => { setOpen(true); setDetail(banner.id) }}>Read</button>
          </div>
        )}
        {['Hours this week', 'Crew on the clock', 'Pending approvals'].map((label) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <b>{label}</b>
            <div style={{ height: 10, borderRadius: 5, background: '#f3f4f6', width: '60%' }} />
            <div style={{ height: 10, borderRadius: 5, background: '#f3f4f6' }} />
          </div>
        ))}
      </div>
      {open && (
        <aside aria-label="News" style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(440px, 100%)', background: '#fff', borderLeft: '1px solid #e5e7eb', boxShadow: '-10px 0 30px rgba(0,0,0,.08)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 10 }}>
            {current ? (
              <button type="button" style={btn} onClick={() => setDetail(null)}><ArrowLeftIcon style={{ width: 14, height: 14 }} />Back</button>
            ) : (
              <>
                <span style={{ width: 32, height: 32, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'rgba(249,115,22,.14)' }}><NewspaperIcon style={{ width: 16, height: 16, color: '#ea580c' }} /></span>
                <b style={{ fontSize: 16 }}>News</b>
              </>
            )}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {!current && <button type="button" style={btn}>Mark all read</button>}
              <button type="button" style={btn} aria-label="Close" onClick={() => setOpen(false)}><XMarkIcon style={{ width: 14, height: 14 }} /></button>
            </span>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {current ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {current.cover_image_url && <Cover item={current} height={140} radius={14} />}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <CategoryPill category={current.category} />
                  {current.version && <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 8px', background: '#f1f5f9', color: '#475569' }}>v{current.version}</span>}
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>{current.date}</span>
                </div>
                <h2 style={{ margin: 0, fontSize: 19 }}>{current.title || 'Untitled'}</h2>
                {current.summary && <p style={{ margin: 0, color: '#6b7280' }}>{current.summary}</p>}
                <BlockRenderer blocks={current.blocks} tokens={t} accent={CATEGORIES[current.category].color} />
              </div>
            ) : items.map((x) => (
              <button key={x.id} type="button" onClick={() => setDetail(x.id)} style={{ display: 'grid', gridTemplateColumns: '36px minmax(0,1fr) auto', gap: 10, alignItems: 'start', padding: 10, borderRadius: 12, border: `1px solid ${x.unread ? '#fed7aa' : 'transparent'}`, background: x.unread ? '#fffaf5' : '#fff', cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit', width: '100%', opacity: x.sample ? 0.75 : 1 }}>
                <Chip category={x.category} size={36} radius={10} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {x.unread && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f97316', display: 'inline-block' }} />}
                    {x.title || 'Untitled'}
                    {x.pinned && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 8px', background: '#f1f5f9', color: '#475569' }}><BookmarkIcon style={{ width: 11, height: 11 }} />Pinned</span>}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#6b7280', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{x.summary}</div>
                  <div style={{ marginTop: 4 }}><CategoryPill category={x.category} /></div>
                </div>
                <span style={{ fontSize: 11.5, color: '#9ca3af', whiteSpace: 'nowrap' }}>{x.date}{x.version ? <><br />v{x.version}</> : null}</span>
              </button>
            ))}
          </div>
        </aside>
      )}
    </div>
  )
}

type Tab = 'app-list' | 'app-article' | 'app-home' | 'portal'

export default function NewsPreviews({ item }: { item: PreviewItem }) {
  const [tab, setTab] = useState<Tab>('app-list')
  const [dark, setDark] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const t = dark ? APP_DARK : APP_LIGHT
  const onApp = item.audience !== 'portal'
  const onPortal = item.audience !== 'app'
  const pinnedFirst = (list: PreviewItem[]) => [...list].sort((a, b) => Number(b.pinned) - Number(a.pinned))
  const appItems = pinnedFirst([...(onApp ? [item] : []), ...SAMPLES.filter((s) => s.audience !== 'portal')])
  const portalItems = pinnedFirst([...(onPortal ? [item] : []), ...SAMPLES.filter((s) => s.audience !== 'app')])
  const opened = appItems.find((x) => x.id === openId) || item

  const seg = (on: boolean): CSSProperties => ({ border: 0, background: on ? '#fff' : 'transparent', padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, color: on ? '#0f172a' : '#475569', cursor: 'pointer', boxShadow: on ? '0 1px 2px rgba(0,0,0,.08)' : 'none' })
  const tabs: Array<[Tab, string]> = [['app-list', 'App list'], ['app-article', 'App article'], ['app-home', 'App home'], ['portal', 'Admin portal']]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {tabs.map(([k, label]) => <button key={k} type="button" style={seg(tab === k)} aria-pressed={tab === k} onClick={() => { setTab(k); if (k === 'app-article') setOpenId(item.id) }}>{label}</button>)}
        </div>
        {tab !== 'portal' && (
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            <button type="button" style={seg(!dark)} aria-pressed={!dark} onClick={() => setDark(false)}>Light</button>
            <button type="button" style={seg(dark)} aria-pressed={dark} onClick={() => setDark(true)}>Dark</button>
          </div>
        )}
      </div>
      {tab !== 'portal' && !onApp && <p className="text-xs text-amber-700">Not shown in the employee app: "Where" is set to the admin portal.</p>}
      {tab === 'portal' && !onPortal && <p className="text-xs text-amber-700">Not shown in the admin portal: "Where" is set to the employee app.</p>}
      <div className="rounded-xl bg-stone-200 p-3.5 flex justify-center min-h-[420px]">
        {tab === 'app-list' && <AppList items={appItems} t={t} onOpen={(id) => { setOpenId(id); setTab('app-article') }} />}
        {tab === 'app-article' && <AppArticle item={opened} t={t} onBack={() => setTab('app-list')} />}
        {tab === 'app-home' && <AppHome items={appItems} t={t} onOpen={(id) => { setOpenId(id); setTab('app-article') }} />}
        {tab === 'portal' && <PortalPreview items={portalItems} />}
      </div>
    </div>
  )
}
